import { useState } from "react";
import { MessageSquareText, Loader2, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api, { formatApiErrorDetail } from "@/lib/api";

const TONES = [
    { id: "trendy", label: "Trendy" },
    { id: "professional", label: "Professional" },
    { id: "casual", label: "Casual" },
    { id: "luxury", label: "Luxury" },
];

const LANGS = [
    { id: "en", label: "English" },
    { id: "es", label: "Spanish" },
];

const PROVIDERS = [
    { id: "openai", model: "gpt-5.2", label: "GPT-5.2", tag: "OpenAI" },
    { id: "anthropic", model: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5", tag: "Anthropic" },
    { id: "gemini", model: "gemini-3-flash-preview", label: "Gemini 3 Flash", tag: "Google" },
];

export default function Captions() {
    const [description, setDescription] = useState("");
    const [tone, setTone] = useState("trendy");
    const [language, setLanguage] = useState("en");
    const [provider, setProvider] = useState(PROVIDERS[0]);
    const [output, setOutput] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (!description.trim()) {
            toast.error("Describe your dish first");
            return;
        }
        setLoading(true);
        setOutput("");
        try {
            const { data } = await api.post("/captions/generate", {
                description,
                tone,
                language,
                provider: provider.id,
                model: provider.model,
            });
            setOutput(data.output);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI failed");
        } finally {
            setLoading(false);
        }
    };

    const copyOutput = () => {
        navigator.clipboard.writeText(output);
        toast.success("Copied!");
    };

    return (
        <AppShell subtitle="AI Caption Generator" title="Captions that crave clicks.">
            <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-5 space-y-6">
                    <div className="glass-card rounded-2xl p-6">
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Dish description</div>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            placeholder="Slow-smoked brisket tacos with charred salsa verde and fresh cilantro lime crema"
                            data-testid="captions-description"
                            className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60 text-sm resize-none"
                        />
                    </div>
                    <div className="glass-card rounded-2xl p-6">
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Tone</div>
                        <div className="flex flex-wrap gap-2 mb-5">
                            {TONES.map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setTone(t.id)}
                                    data-testid={`captions-tone-${t.id}`}
                                    className={`px-3.5 py-1.5 rounded-full text-sm border transition ${tone === t.id ? "bg-orange-500/15 border-orange-500/40 text-orange-300" : "bg-white/3 border-white/10 text-zinc-400 hover:bg-white/8"}`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Language</div>
                        <div className="flex gap-2">
                            {LANGS.map((l) => (
                                <button
                                    key={l.id}
                                    type="button"
                                    onClick={() => setLanguage(l.id)}
                                    data-testid={`captions-lang-${l.id}`}
                                    className={`px-3.5 py-1.5 rounded-full text-sm border transition ${language === l.id ? "bg-orange-500/15 border-orange-500/40 text-orange-300" : "bg-white/3 border-white/10 text-zinc-400 hover:bg-white/8"}`}
                                >
                                    {l.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="glass-card rounded-2xl p-6">
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">AI provider</div>
                        <div className="space-y-2">
                            {PROVIDERS.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => setProvider(p)}
                                    data-testid={`captions-provider-${p.id}`}
                                    className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border text-sm transition ${provider.id === p.id ? "border-orange-500/40 bg-orange-500/8 text-white" : "border-white/10 bg-white/3 text-zinc-400 hover:bg-white/8"}`}
                                >
                                    <span>{p.label}</span>
                                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{p.tag}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={loading}
                        data-testid="captions-generate-btn"
                        className="btn-magic w-full py-4 rounded-2xl text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {loading ? "Writing..." : "Generate captions"}
                    </button>
                </div>
                <div className="lg:col-span-7">
                    <div className="glass-card rounded-3xl p-7 md:p-9 min-h-[420px]">
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Output</div>
                                <h3 className="font-heading text-xl font-semibold">Ready-to-post copy</h3>
                            </div>
                            {output && (
                                <button
                                    type="button"
                                    onClick={copyOutput}
                                    data-testid="captions-copy-btn"
                                    className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs inline-flex items-center gap-1.5"
                                >
                                    <Copy className="w-3 h-3" /> Copy all
                                </button>
                            )}
                        </div>
                        {output ? (
                            <pre className="whitespace-pre-wrap font-sans text-zinc-200 text-sm leading-relaxed" data-testid="captions-output">
                                {output}
                            </pre>
                        ) : (
                            <div className="text-center py-16 text-zinc-500">
                                <MessageSquareText className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                                <div>Captions, hashtags, SMS promo & menu copy will appear here.</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
